import { useCallback, useMemo } from "react";
import { enums } from "@shared/constants";
import useHoldDetails from "@app-contexts/user/features/basket/data/services/useHoldDetails";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import type { GetBasketHoldParamType } from "@app-contexts/user/features/basket/data/types/basket";
import type { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { useHoldQuantity } from "./useHoldQuantity";

type UseReviewItemGroupHoldStateOptions = {
  holdSlug: string | undefined;
  dynamicHoldParams: GetBasketHoldParamType | undefined;
  itemGroup: TicketItemGroupType;
  allowQuantityChange: boolean;
  onOpenSeatingModal: () => void;
  onCloseSeatingModal: () => void;
};

type UseReviewItemGroupHoldStateReturn = {
  effectiveAllowQuantityChange: boolean;
  holdMinQuantity: number | null | undefined;
  holdMaxQuantity: number | null | undefined;
  handleOpenAssignedSeatingModal: () => void;
  handleCloseAssignedSeatingModal: () => void;
};

export function useReviewItemGroupHoldState({
  holdSlug,
  dynamicHoldParams,
  itemGroup,
  allowQuantityChange,
  onOpenSeatingModal,
  onCloseSeatingModal,
}: UseReviewItemGroupHoldStateOptions): UseReviewItemGroupHoldStateReturn {
  const { data: holdDetailsData } = useHoldDetails(
    holdSlug ?? "",
    dynamicHoldParams,
    { enabled: Boolean(holdSlug) }
  );

  const { setDynamicHoldSlug, updateDynamicHoldSlug, setBasketHoldID } =
    useSeatingState();

  const holdType = holdDetailsData?.on_hold_detail?.hold_type;
  const trackingSlug = holdDetailsData?.on_hold_detail?.tracking_link_slug;
  const holdId = holdDetailsData?.id;

  const isItemGroupFromDynamicHold = useMemo(
    () =>
      holdType === enums.BASKET_ON_HOLD_TYPE_MAP.DYNAMIC.id &&
      Boolean(trackingSlug) &&
      holdId != null &&
      Boolean(
        holdDetailsData?.item_groups?.some((ig) => ig.type === itemGroup.type)
      ),
    [
      holdType,
      trackingSlug,
      holdId,
      holdDetailsData?.item_groups,
      itemGroup.type,
    ]
  );

  const { holdEnforcesQuantityChange, holdMinQuantity, holdMaxQuantity } =
    useHoldQuantity(holdDetailsData?.on_hold_detail);

  const effectiveAllowQuantityChange =
    holdEnforcesQuantityChange ?? allowQuantityChange;

  const handleOpenAssignedSeatingModal = useCallback(() => {
    if (isItemGroupFromDynamicHold) {
      updateDynamicHoldSlug(trackingSlug ?? "");
      setBasketHoldID(holdId ?? undefined);
    }
    onOpenSeatingModal();
  }, [
    isItemGroupFromDynamicHold,
    trackingSlug,
    holdId,
    onOpenSeatingModal,
    updateDynamicHoldSlug,
    setBasketHoldID,
  ]);

  const handleCloseAssignedSeatingModal = useCallback(() => {
    onCloseSeatingModal();
    setDynamicHoldSlug(undefined);
    setBasketHoldID(undefined);
  }, [onCloseSeatingModal, setDynamicHoldSlug, setBasketHoldID]);

  return {
    effectiveAllowQuantityChange,
    holdMinQuantity,
    holdMaxQuantity,
    handleOpenAssignedSeatingModal,
    handleCloseAssignedSeatingModal,
  };
}
